import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  type ReactNode,
} from 'react'
import { authApi, getToken, setToken, removeToken, getStoredUser, setStoredUser, removeStoredUser } from '@/lib/api'
import type { User, LoginCredentials, UserRole } from '@/types'

interface AuthContextValue {
  user: User | null
  token: string | null
  role: UserRole | null
  isAuthenticated: boolean
  isLoading: boolean
  login: (credentials: LoginCredentials) => Promise<void>
  logout: () => Promise<void>
}

const AuthContext = createContext<AuthContextValue | null>(null)

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null)
  const [token, setTokenState] = useState<string | null>(getToken)
  const [isLoading, setIsLoading] = useState<boolean>(true)

  // Al montar, si hay token intenta recuperar el usuario
  useEffect(() => {
    const storedToken = getToken()
    if (!storedToken) {
      setIsLoading(false)
      return
    }

    authApi
      .me()
      .then((res) => {
        setUser(res.data)
        setStoredUser(res.data)
        setTokenState(storedToken)
      })
      .catch((error) => {
        const isNetworkError = !error.response
        if (isNetworkError) {
          // Sin conexión: restaurar sesión desde caché para permitir operación offline
          const cachedUser = getStoredUser()
          if (cachedUser) {
            setUser(cachedUser)
            setTokenState(storedToken)
          } else {
            removeToken()
            setTokenState(null)
            setUser(null)
          }
        } else {
          // Error de autenticación (401, etc.): limpiar sesión
          removeToken()
          removeStoredUser()
          setTokenState(null)
          setUser(null)
        }
      })
      .finally(() => setIsLoading(false))
  }, [])

  const login = useCallback(async (credentials: LoginCredentials) => {
    const res = await authApi.login(credentials)
    const { token: newToken, user: newUser } = res.data
    setToken(newToken)
    setStoredUser(newUser)
    setTokenState(newToken)
    setUser(newUser)
  }, [])

  const logout = useCallback(async () => {
    try {
      await authApi.logout()
    } catch {
      // Ignorar error de red en logout
    } finally {
      removeToken()
      removeStoredUser()
      setTokenState(null)
      setUser(null)
    }
  }, [])

  const value: AuthContextValue = {
    user,
    token,
    role: user?.role ?? null,
    isAuthenticated: Boolean(user && token),
    isLoading,
    login,
    logout,
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext)
  if (!ctx) {
    throw new Error('useAuth debe usarse dentro de <AuthProvider>')
  }
  return ctx
}
